import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Maximize2, Image as ImageIcon } from 'lucide-react';
import ZoomableImage from './ZoomableImage';
import ConsumablesPiles from './ConsumablesPiles';

interface Diagram {
  url?: string;
  image_url?: string;
  title?: string;
  description?: string;
  source?: string;
  generated?: boolean;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  diagrams?: Diagram[];
  evidence_context?: any[];
  confidence_score?: number;
  technical_data?: {
    maintenance?: any;
    vehicle_info?: any;
  };
}

interface MarkdownMessageProps {
  message: Message;
}

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ message }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Function to scroll to sources section
  const scrollToSources = () => {
    const sourcesElement = document.getElementById(`sources-${message.role}-${message.content.length}`);
    if (sourcesElement) {
      sourcesElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // We no longer strip citations. We can optionally wrap them in something clickable.
  // For now, let's just ensure they are rendered and the Sources section is identifiable.
  const displayContent = message.content;

  return (
    <div className={`space-y-4`}>
      <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-4xl w-full rounded-2xl p-6 ${message.role === 'user'
            ? 'bg-gradient-to-br from-secondary/20 to-primary/20 text-white font-medium shadow-xl border border-primary/20'
            : 'bg-dark/60 backdrop-blur-xl text-gray-200 border border-slate/30 shadow-2xl'
            }`}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ ...props }) => <h1 className="text-3xl font-black text-white mb-4 uppercase tracking-tighter border-b border-primary/20 pb-2" {...props} />,
              h2: ({ ...props }) => <h2 className="text-2xl font-black text-primary mb-3 uppercase tracking-tight" {...props} />,
              h3: ({ ...props }) => <h3 className="text-xl font-bold text-accent mb-2 uppercase tracking-wide" {...props} />,
              strong: ({ ...props }) => <strong className="font-bold text-primary" {...props} />,
              em: ({ ...props }) => <em className="italic text-accent/80" {...props} />,
              code: ({ inline, ...props }: any) =>
                inline ? (
                  <code className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-mono text-sm" {...props} />
                ) : (
                  <code className="block bg-black/40 text-primary p-4 rounded-xl my-4 overflow-x-auto border border-primary/10 font-mono text-sm shadow-inner" {...props} />
                ),
              ul: ({ ...props }) => <ul className="list-disc list-outside my-4 space-y-2 ml-4" {...props} />,
              ol: ({ ...props }) => <ol className="list-decimal list-outside my-4 space-y-2 ml-4" {...props} />,
              li: ({ ...props }) => <li className="text-gray-300 pl-2" {...props} />,
              a: ({ ...props }) => <a target="_blank" rel="noopener noreferrer" className="text-primary hover:text-white underline underline-offset-4 decoration-primary/30 hover:decoration-white transition-all font-black uppercase tracking-widest text-[10px]" {...props} />,
              blockquote: ({ ...props }) => (
                <blockquote className="border-l-4 border-primary/50 pl-6 py-2 italic text-gray-400 my-6 bg-primary/5 rounded-r-xl" {...props} />
              ),
              p: ({ ...props }) => {
                const content = props.children?.toString() || '';
                // Check if this is the "SOURCES & LINKS" or "Sources & Confidence" section
                if (content.includes('SOURCES & LINKS') || content.includes('TECHNICAL RESOURCES') || content.includes('SOURCES & CONFIDENCE') || content.includes('Sources & Confidence')) {
                  return <p id={`sources-${message.role}-${message.content.length}`} className="text-lg font-black text-primary mt-8 mb-4 uppercase tracking-[0.2em] border-t border-primary/20 pt-6" {...props} />;
                }

                // Enhance citations within paragraph text
                const children = React.Children.map(props.children, child => {
                  if (typeof child === 'string') {
                    const parts = child.split(/(\[\d+\])/g);
                    return parts.map((part, i) => {
                      if (part.match(/\[\d+\]/)) {
                        return (
                          <span
                            key={i}
                            onClick={scrollToSources}
                            className="inline-flex items-center justify-center bg-primary/10 text-primary hover:bg-primary hover:text-dark px-1.5 py-0.5 rounded text-[10px] font-black cursor-pointer transition-all mx-0.5 align-top"
                          >
                            {part}
                          </span>
                        );
                      }
                      return part;
                    });
                  }
                  return child;
                });
                return <p className="mb-4 leading-relaxed" {...props}>{children}</p>;
              },
              table: ({ ...props }) => (
                <div className="overflow-x-auto my-6 rounded-xl border border-slate/20">
                  <table className="w-full text-left border-collapse" {...props} />
                </div>
              ),
              th: ({ ...props }) => <th className="bg-primary/10 p-3 text-white font-black uppercase tracking-widest text-xs border-b border-slate/20" {...props} />,
              td: ({ ...props }) => <td className="p-3 text-gray-400 border-b border-slate/20/50 text-sm" {...props} />,
            }}
          >
            {displayContent}
          </ReactMarkdown>
        </div>
      </div>

      {/* Maintenance Consumables (Electronics or Automotive) */}
      {message.role === 'assistant' && message.technical_data?.maintenance && (
        <div className="mt-4 animate-in fade-in slide-in-from-bottom-2 duration-700">
          <ConsumablesPiles 
            vehicleContext={message.technical_data.vehicle_info} 
            maintenanceSpec={message.technical_data.maintenance} 
          />
        </div>
      )}

      {/* Evidence Drawer - Pro Move */}
      {(() => {
        const evidenceList = Array.isArray(message.evidence_context) ? message.evidence_context : [];
        if (evidenceList.length === 0) return null;

        return (
          <div className="mt-4 border border-primary/20 rounded-xl overflow-hidden bg-primary/5 backdrop-blur-md">
            <button 
              onClick={() => {
                const el = document.getElementById(`evidence-${message.content.length}`);
                if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
              }}
              className="w-full flex items-center justify-between p-3 text-[10px] font-black uppercase tracking-[0.2em] text-primary hover:bg-primary/10 transition-all font-mono"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
                Verified Evidence & Technical Sources
              </div>
              <div className="flex items-center gap-4">
                {message.confidence_score && (
                  <span className="text-secondary opacity-80">
                    Confidence: {(message.confidence_score * 100).toFixed(0)}%
                  </span>
                )}
                <Maximize2 size={12} />
              </div>
            </button>
            
            <div id={`evidence-${message.content.length}`} style={{ display: 'none' }} className="p-4 border-t border-primary/10 bg-black/40">
              <div className="grid grid-cols-1 gap-3">
                {evidenceList.map((evidence: any, i: number) => (
                  <div key={i} className="flex flex-col gap-1 p-3 rounded-lg border border-white/5 bg-white/5 hover:border-primary/20 transition-all">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[10px] font-bold text-white uppercase tracking-wider">{evidence.title || 'Technical Reference'}</span>
                      <span className="text-[8px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-mono uppercase">{evidence.type || 'SOURCE'}</span>
                    </div>
                    {evidence.snippet && (
                      <p className="text-[11px] text-gray-400 line-clamp-2 leading-relaxed italic">
                        "{evidence.snippet}"
                      </p>
                    )}
                    {evidence.url && (
                      <a 
                        href={evidence.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[9px] text-secondary hover:text-primary transition-colors truncate mt-1 underline decoration-secondary/30 underline-offset-2"
                      >
                        {evidence.url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Diagrams Section */}
      {message.role === 'assistant' && message.diagrams && message.diagrams.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {message.diagrams.map((diagram, i) => (
            <div key={i} className={`group bg-dark/40 border rounded-2xl overflow-hidden p-4 space-y-3 hover:border-primary/50 transition-all shadow-lg ${diagram.generated ? 'border-primary/40 RING-1 ring-primary/20' : 'border-slate/30'}`}>
              <div className="flex justify-between items-start">
                <h4 className="text-white text-xs font-bold uppercase tracking-widest truncate max-w-[200px]">
                  {diagram.generated && <span className="text-primary mr-1.5">✦</span>}
                  {diagram.title || 'Diagnostic Schematic'}
                </h4>
                {diagram.generated && (
                  <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded-full font-black uppercase tracking-tighter">AI Master</span>
                )}
              </div>

              <div
                className="aspect-video bg-black/80 rounded-xl overflow-hidden flex items-center justify-center border border-white/5 relative cursor-zoom-in"
                onClick={() => {
                  const url = diagram.url || diagram.image_url;
                  if (url) setSelectedImage(url);
                }}
              >
                {(diagram.url || diagram.image_url) ? (
                  <img
                    src={diagram.url || diagram.image_url}
                    alt={diagram.title}
                    className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-gray-700">
                    <ImageIcon size={32} />
                    <span className="text-[8px] uppercase font-bold tracking-[0.2em]">Reference Only</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors flex items-center justify-center">
                  <Maximize2 size={20} className="text-white opacity-0 group-hover:opacity-100 transition-all duration-300" />
                </div>
              </div>

              {diagram.description && (
                <p className="text-[10px] text-gray-500 line-clamp-2 leading-relaxed">
                  {diagram.description}
                </p>
              )}

              <div className="pt-2 flex justify-between items-center border-t border-white/10">
                <span className="text-[8px] uppercase font-black tracking-widest text-gray-600">{diagram.source || 'Technical Core'}</span>
                <button
                  onClick={() => {
                    const url = diagram.url || diagram.image_url;
                    if (url) setSelectedImage(url);
                  }}
                  className="text-[9px] uppercase font-black tracking-widest text-primary hover:text-white transition-colors"
                >
                  View full Schematic
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full Resolution Schematic Portal */}
      {selectedImage && (
        <div className="fixed inset-0 z-[100] bg-dark/95 backdrop-blur-2xl flex items-center justify-center p-4 animate-in fade-in duration-300">
          <button
            className="absolute top-8 right-8 z-[110] text-gray-400 hover:text-white transition-all hover:rotate-90"
            onClick={() => setSelectedImage(null)}
          >
            <ImageIcon size={32} />
          </button>
          <div className="w-full max-w-5xl animate-in zoom-in-95 duration-500">
            <ZoomableImage src={selectedImage} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkdownMessage;

